import { WHATSAPP_PROVIDER_TO_PLATFORM_NAME_MAPPER } from 'shared/consts/consts';
import { IObjProps } from 'shared/consts/types';
import { BROADCAST_TARGET_GROUP } from 'shared/enum';

import {
	RetargetOutcome,
	VoiceCampaignChannel,
	VoiceCampaignContactPlatform,
	VoiceCampaignMedium,
	VoiceCampaignType,
} from './constants';

export const CREATE_VOICE_CAMPAIGN = {
	AGENT: 'agent',
	AUTOMATIC_CALLBACK_ENABLED: 'automaticCallbackEnabled',
	AUDIENCE_SOURCE: 'audienceSource',
	CAMPAIGN_NAME: 'campaignName',
	END_DATE: 'campaignEndDate',
	MAX_PARALLEL_SLOTS: 'maxParallelSlots',
	OPERATING_END_TIME: 'dailyEndTime',
	OPERATING_START_TIME: 'dailyStartTime',
	SELECTED_CONTACT_LIST: 'selectedContactList',
	START_DATE: 'campaignStartDate',
	TARGET_CHANNEL: 'targetChannel',
	CHILD_CAMPAIGNS: 'childCampaigns',
} as const;

export type VoiceCampaignOption = {
	content?: IObjProps;
	id: string;
	label: string;
};

export type CreateVoiceCampaignFormValues = {
	[CREATE_VOICE_CAMPAIGN.AGENT]: VoiceCampaignOption[] | null;
	[CREATE_VOICE_CAMPAIGN.AUTOMATIC_CALLBACK_ENABLED]: boolean;
	[CREATE_VOICE_CAMPAIGN.AUDIENCE_SOURCE]: VoiceCampaignAudienceSourceValue;
	[CREATE_VOICE_CAMPAIGN.CAMPAIGN_NAME]: string;
	[CREATE_VOICE_CAMPAIGN.END_DATE]: Date | null;
	[CREATE_VOICE_CAMPAIGN.MAX_PARALLEL_SLOTS]: string;
	[CREATE_VOICE_CAMPAIGN.OPERATING_END_TIME]: Date;
	[CREATE_VOICE_CAMPAIGN.OPERATING_START_TIME]: Date;
	[CREATE_VOICE_CAMPAIGN.SELECTED_CONTACT_LIST]: VoiceCampaignOption[] | null;
	[CREATE_VOICE_CAMPAIGN.START_DATE]: Date | null;
	[CREATE_VOICE_CAMPAIGN.TARGET_CHANNEL]: VoiceCampaignOption[] | null;
	[CREATE_VOICE_CAMPAIGN.CHILD_CAMPAIGNS]: ScheduledRetargetCampaignFormValues[];
};

export type ScheduledRetargetCampaignFormValues = {
	agent: VoiceCampaignOption[] | null;
	campaignName: string;
	dailyEndTime: Date;
	dailyStartTime: Date;
	maxParallelSlots: string;
	outcomes: RetargetOutcome[];
	dispositions: string[];
	targetChannel: VoiceCampaignOption[] | null;
	followUpCount: string;
	followUpIntervalHours: string;
	schedules: ScheduledRetargetScheduleFormValues[];
};

export const VoiceCampaignAudienceSource = {
	CONTACT_LIST: 'CONTACT_LIST',
	EXTERNAL_API: 'EXTERNAL_API',
} as const;

export type VoiceCampaignAudienceSourceValue =
	(typeof VoiceCampaignAudienceSource)[keyof typeof VoiceCampaignAudienceSource];

export type ScheduledRetargetScheduleFormValues = {
	campaignStartDate: Date;
	campaignEndDate: Date;
	sequence: number;
};

export type ScheduledRetargetCampaignOccurrence = ScheduledRetargetCampaignFormValues &
	ScheduledRetargetScheduleFormValues & {
		retargetGroupId?: number | string;
	};

export type RetargetCampaignContext = {
	parentCampaignId?: string;
	retargetGroupId?: number | string;
	outcomes: RetargetOutcome[];
	dispositions?: string[];
	sourceCampaign: IObjProps;
};

const VOICE_CAMPAIGN_CHANNEL_ICON_KEY_MAPPER = {
	[VoiceCampaignChannel.TELEPHONY]: 'telephony',
	[VoiceCampaignChannel.WHATSAPP]: 'whatsapp',
} as const;

const roundUpToNextInterval = (value: Date, intervalInMinutes: number) => {
	const roundedValue = new Date(value);
	const minutes = roundedValue.getMinutes();
	const remainder = minutes % intervalInMinutes;

	if (remainder) {
		roundedValue.setMinutes(minutes + intervalInMinutes - remainder);
	}

	roundedValue.setSeconds(0, 0);

	return roundedValue;
};

const createDefaultStartDate = () => roundUpToNextInterval(new Date(), 15);

const createDefaultEndDate = (startDate: Date) => {
	const value = new Date(startDate);
	value.setHours(23, 45, 0, 0);

	return value.getTime() >= startDate.getTime() ? value : startDate;
};

const getVoiceCampaignChannelKey = (channel?: string) => {
	const normalizedChannel = channel?.trim().toUpperCase();

	if (!normalizedChannel) {
		return '';
	}

	return (
		VOICE_CAMPAIGN_CHANNEL_ICON_KEY_MAPPER[
			normalizedChannel as keyof typeof VOICE_CAMPAIGN_CHANNEL_ICON_KEY_MAPPER
		] || normalizedChannel.toLowerCase()
	);
};

export const getVoiceCampaignFormattedLabel = (value?: string) =>
	value
		?.trim()
		.toLowerCase()
		.replace(/_/g, ' ')
		.replace(/\b\w/g, (character) => character.toUpperCase()) || '-';

export const CREATE_VOICE_CAMPAIGN_DEFAULT_CONTENT_SELECTOR = {
	whatsapp: { permissionTemplate: { mode: 'DEFAULT' } },
};

export const MAX_PARALLEL_SLOTS = 10;
export const MAX_RETARGET_CAMPAIGNS = 20;
export const MAX_CAMPAIGN_DURATION_IN_DAYS = 7;
export const MAX_EXTERNAL_CAMPAIGN_DURATION_IN_DAYS = 30;

const padDateValue = (value: number) => `${value}`.padStart(2, '0');

export const localTimeString = (value: Date) =>
	`${padDateValue(value.getHours())}:${padDateValue(value.getMinutes())}:${padDateValue(value.getSeconds())}`;

export const utcDateTimeString = (value: Date) => value.toISOString();

export const utcTimeString = (value: Date) => value.toISOString().split('T')[1].slice(0, 5);

export function createTimeValue(hours: number, minutes: number) {
	const value = new Date();
	value.setHours(hours, minutes, 0, 0);

	return value;
}

export const getTimeValueInMinutes = (value: Date | null) =>
	value ? value.getHours() * 60 + value.getMinutes() + value.getSeconds() / 60 : -1;

export const getUtcTimeValueInMinutes = (value: Date | null) =>
	value ? value.getUTCHours() * 60 + value.getUTCMinutes() + value.getUTCSeconds() / 60 : -1;

export const getResolvedStartDate = (values: CreateVoiceCampaignFormValues) =>
	values[CREATE_VOICE_CAMPAIGN.START_DATE] || new Date();

export const getResolvedEndDate = (values: CreateVoiceCampaignFormValues, startDate: Date) =>
	values[CREATE_VOICE_CAMPAIGN.END_DATE] || startDate;

export const getMaxCampaignEndDate = (startDate: Date, isExternalCampaign = false) => {
	const maxEndDate = new Date(startDate);
	maxEndDate.setDate(
		maxEndDate.getDate() +
			(isExternalCampaign ? MAX_EXTERNAL_CAMPAIGN_DURATION_IN_DAYS : MAX_CAMPAIGN_DURATION_IN_DAYS)
	);

	return maxEndDate;
};

export const getCreateVoiceCampaignDefaultValues = (): CreateVoiceCampaignFormValues => {
	const startDate = createDefaultStartDate();

	return {
		[CREATE_VOICE_CAMPAIGN.AGENT]: [],
		[CREATE_VOICE_CAMPAIGN.AUTOMATIC_CALLBACK_ENABLED]: false,
		[CREATE_VOICE_CAMPAIGN.AUDIENCE_SOURCE]: VoiceCampaignAudienceSource.CONTACT_LIST,
		[CREATE_VOICE_CAMPAIGN.CAMPAIGN_NAME]: '',
		[CREATE_VOICE_CAMPAIGN.END_DATE]: createDefaultEndDate(startDate),
		[CREATE_VOICE_CAMPAIGN.MAX_PARALLEL_SLOTS]: '3',
		[CREATE_VOICE_CAMPAIGN.OPERATING_END_TIME]: createTimeValue(19, 0),
		[CREATE_VOICE_CAMPAIGN.OPERATING_START_TIME]: createTimeValue(10, 0),
		[CREATE_VOICE_CAMPAIGN.SELECTED_CONTACT_LIST]: [],
		[CREATE_VOICE_CAMPAIGN.START_DATE]: startDate,
		[CREATE_VOICE_CAMPAIGN.TARGET_CHANNEL]: [],
		[CREATE_VOICE_CAMPAIGN.CHILD_CAMPAIGNS]: [],
	};
};

export const getScheduledRetargetDefaultValues = ({
	agent,
	campaignName,
	dailyEndTime,
	dailyStartTime,
	maxParallelSlots,
	targetChannel,
}: {
	agent: VoiceCampaignOption[] | null;
	campaignName: string;
	dailyEndTime: Date;
	dailyStartTime: Date;
	maxParallelSlots: string;
	targetChannel: VoiceCampaignOption[] | null;
}): ScheduledRetargetCampaignFormValues => ({
	agent: agent ? [...agent] : [],
	campaignName,
	dailyEndTime: new Date(dailyEndTime),
	dailyStartTime: new Date(dailyStartTime),
	maxParallelSlots,
	outcomes: [],
	dispositions: [],
	targetChannel: targetChannel ? [...targetChannel] : [],
	followUpCount: '',
	followUpIntervalHours: '',
	schedules: [],
});

export const generateScheduledRetargetDates = (
	campaignCountValue: string,
	intervalHoursValue: string,
	parentStartDate: Date,
	parentEndDate: Date
): ScheduledRetargetScheduleFormValues[] => {
	const campaignCount = Math.min(MAX_RETARGET_CAMPAIGNS, Math.max(0, Math.floor(Number(campaignCountValue) || 0)));
	const intervalInMilliseconds = Math.max(0, Number(intervalHoursValue) || 0) * 60 * 60 * 1000;
	const campaignDuration = parentEndDate.getTime() - parentStartDate.getTime();

	if (!campaignCount || !intervalInMilliseconds || campaignDuration <= 0) {
		return [];
	}

	return Array.from({ length: campaignCount }, (_, index) => {
		const campaignStartDate = new Date(
			parentStartDate.getTime() + (index + 1) * intervalInMilliseconds + index * campaignDuration
		);

		return {
			campaignStartDate,
			campaignEndDate: new Date(campaignStartDate.getTime() + campaignDuration),
			sequence: index + 1,
		};
	});
};

export const getScheduledRetargetOccurrences = (
	values: ScheduledRetargetCampaignFormValues,
	parentStartDate: Date,
	parentEndDate: Date
): ScheduledRetargetCampaignOccurrence[] => {
	const generatedSchedules = generateScheduledRetargetDates(
		values.followUpCount,
		values.followUpIntervalHours,
		parentStartDate,
		parentEndDate
	);
	const configuredSchedules =
		values.schedules?.length === generatedSchedules.length &&
		values.schedules.every((schedule) => schedule.campaignStartDate && schedule.campaignEndDate)
			? values.schedules
			: generatedSchedules;

	return configuredSchedules.map((schedule, index) => ({
		...values,
		...schedule,
		campaignName: configuredSchedules.length > 1 ? `${values.campaignName} - ${index + 1}` : values.campaignName,
	}));
};

export const getVoiceCampaignContactPlatform = (channelId: string, whatsappProvider?: string) => {
	if (channelId === VoiceCampaignChannel.WHATSAPP.toLowerCase()) {
		return (
			WHATSAPP_PROVIDER_TO_PLATFORM_NAME_MAPPER[whatsappProvider || ''] || VoiceCampaignContactPlatform.DIALOG360
		);
	}

	return VoiceCampaignContactPlatform.TELEPHONY;
};

export const isVoiceCampaignContactListTargetGroup = (targetGroup?: string) =>
	targetGroup === BROADCAST_TARGET_GROUP.CONTACT_LIST;

export const isOriginalVoiceCampaign = (campaign?: IObjProps | null) =>
	((campaign?.type === 'DEFAULT' && isVoiceCampaignContactListTargetGroup(campaign?.targetGroup)) ||
		(campaign?.type === VoiceCampaignType.EXTERNAL &&
			campaign?.targetGroup === BROADCAST_TARGET_GROUP.EXTERNAL_API)) &&
	!campaign?.parentCampaignId &&
	campaign?.medium === VoiceCampaignMedium.VOICE &&
	[VoiceCampaignChannel.TELEPHONY, VoiceCampaignChannel.WHATSAPP].includes(campaign?.channel);

export const getSelectedContactMetadataId = (selectedContact?: VoiceCampaignOption) => {
	const content = selectedContact?.content || {};

	return content.contactMetadataId || selectedContact?.id;
};

export const getSelectedContactMetadataName = (selectedContact?: VoiceCampaignOption) => {
	const content = selectedContact?.content || {};

	return content?.contactMetadataName || selectedContact?.label || '';
};

const getSelectedAgentId = (selectedAgent?: VoiceCampaignOption) => {
	const content = selectedAgent?.content || {};

	return content.agentId || selectedAgent?.id;
};

type BuildVoiceCampaignPayloadArgs = {
	botRef: number | string;
	currentUserId: number | string;
	customerId: number | string;
	values: CreateVoiceCampaignFormValues;
	retargetContext?: RetargetCampaignContext | null;
};

export const buildVoiceCampaignPayload = ({
	botRef,
	currentUserId,
	customerId,
	values,
	retargetContext,
}: BuildVoiceCampaignPayloadArgs): IObjProps => {
	const selectedContact = values[CREATE_VOICE_CAMPAIGN.SELECTED_CONTACT_LIST]?.[0];
	const selectedAgent = values[CREATE_VOICE_CAMPAIGN.AGENT]?.[0];
	const contactMetadataId = getSelectedContactMetadataId(selectedContact);
	const contactMetadataName = getSelectedContactMetadataName(selectedContact);
	const agentId = getSelectedAgentId(selectedAgent);
	const selectedChannelId = values[CREATE_VOICE_CAMPAIGN.TARGET_CHANNEL]?.[0]?.id;
	const startDate = getResolvedStartDate(values);
	const endDate = getResolvedEndDate(values, startDate);
	const resolvedChannel = selectedChannelId?.toString().toUpperCase() || VoiceCampaignChannel.TELEPHONY;
	const contentSelector =
		resolvedChannel === VoiceCampaignChannel.TELEPHONY ? {} : CREATE_VOICE_CAMPAIGN_DEFAULT_CONTENT_SELECTOR;
	const isExternalCampaign =
		values[CREATE_VOICE_CAMPAIGN.AUDIENCE_SOURCE] === VoiceCampaignAudienceSource.EXTERNAL_API;

	const commonPayload = {
		customerId,
		botRef,
		name: values[CREATE_VOICE_CAMPAIGN.CAMPAIGN_NAME],
		type: isExternalCampaign ? VoiceCampaignType.EXTERNAL : VoiceCampaignType.DEFAULT,
		medium: VoiceCampaignMedium.VOICE,
		triggerStartAt: utcDateTimeString(startDate),
		completionDeadlineAt: utcDateTimeString(endDate),
		dailyStartTime: utcTimeString(values[CREATE_VOICE_CAMPAIGN.OPERATING_START_TIME]),
		dailyEndTime: utcTimeString(values[CREATE_VOICE_CAMPAIGN.OPERATING_END_TIME]),
		channel: resolvedChannel,
		targetGroup: isExternalCampaign ? BROADCAST_TARGET_GROUP.EXTERNAL_API : BROADCAST_TARGET_GROUP.CONTACT_LIST,
		targetGroupSelector: isExternalCampaign
			? {}
			: {
					contactMetadataId: Number(contactMetadataId),
					contactMetadataName,
				},
		agentId,
		contentSelector,
		maxParallelSlots: Number(values[CREATE_VOICE_CAMPAIGN.MAX_PARALLEL_SLOTS]),
		createdBy: Number(currentUserId),
		updatedBy: Number(currentUserId),
		automaticCallbackEnabled: values[CREATE_VOICE_CAMPAIGN.AUTOMATIC_CALLBACK_ENABLED],
	};

	if (!retargetContext) {
		const scheduledRetargetCampaigns = (values[CREATE_VOICE_CAMPAIGN.CHILD_CAMPAIGNS] || []).flatMap(
			(childValues) => getScheduledRetargetOccurrences(childValues, startDate, endDate)
		);

		return scheduledRetargetCampaigns.length
			? {
					...commonPayload,
					childCampaigns: scheduledRetargetCampaigns.map((childValues) =>
						buildScheduledRetargetPayload({
							botRef,
							currentUserId,
							customerId,
							parentValues: values,
							values: childValues,
						})
					),
				}
			: commonPayload;
	}

	const numericParentCampaignId = retargetContext.parentCampaignId
		? Number(retargetContext.parentCampaignId)
		: undefined;
	const numericRetargetGroupId = retargetContext.retargetGroupId
		? Number(retargetContext.retargetGroupId)
		: undefined;

	return {
		...commonPayload,
		type: VoiceCampaignType.RETARGET,
		...(Number.isFinite(numericParentCampaignId) ? { parentCampaignId: numericParentCampaignId } : {}),
		...(Number.isFinite(numericRetargetGroupId) ? { retargetGroupId: numericRetargetGroupId } : {}),
		targetGroup: BROADCAST_TARGET_GROUP.USER_SEGMENT_V2,
		targetGroupSelector: {
			type: VoiceCampaignType.RETARGET,
			parentCampaignId: numericParentCampaignId,
			outcomes: retargetContext.outcomes,
			...(retargetContext.dispositions?.length ? { dispositions: retargetContext.dispositions } : {}),
		},
		contentSelector: undefined,
	};
};

export const buildScheduledRetargetPayload = ({
	botRef,
	currentUserId,
	customerId,
	parentCampaignId,
	parentValues,
	values,
}: {
	botRef: number | string;
	currentUserId: number | string;
	customerId: number | string;
	parentCampaignId?: number | string;
	parentValues: CreateVoiceCampaignFormValues;
	values: ScheduledRetargetCampaignOccurrence;
}) => {
	const campaignPayload = buildVoiceCampaignPayload({
		botRef,
		currentUserId,
		customerId,
		values: {
			...parentValues,
			[CREATE_VOICE_CAMPAIGN.AGENT]: values.agent,
			[CREATE_VOICE_CAMPAIGN.CAMPAIGN_NAME]: values.campaignName,
			[CREATE_VOICE_CAMPAIGN.START_DATE]: values.campaignStartDate,
			[CREATE_VOICE_CAMPAIGN.END_DATE]: values.campaignEndDate,
			[CREATE_VOICE_CAMPAIGN.MAX_PARALLEL_SLOTS]: values.maxParallelSlots,
			[CREATE_VOICE_CAMPAIGN.OPERATING_START_TIME]: values.dailyStartTime,
			[CREATE_VOICE_CAMPAIGN.OPERATING_END_TIME]: values.dailyEndTime,
			[CREATE_VOICE_CAMPAIGN.TARGET_CHANNEL]: values.targetChannel,
			[CREATE_VOICE_CAMPAIGN.CHILD_CAMPAIGNS]: [],
			[CREATE_VOICE_CAMPAIGN.AUTOMATIC_CALLBACK_ENABLED]: false,
			[CREATE_VOICE_CAMPAIGN.AUDIENCE_SOURCE]: VoiceCampaignAudienceSource.CONTACT_LIST,
		},
		retargetContext: {
			parentCampaignId: parentCampaignId ? String(parentCampaignId) : undefined,
			retargetGroupId: values.retargetGroupId,
			outcomes: values.outcomes,
			dispositions: values.dispositions,
			sourceCampaign: {},
		},
	});

	return campaignPayload;
};

export const getRetargetCampaignName = (
	sourceName: string,
	outcomeLabels: string[],
	defaultCampaignName: string,
	retargetLabel: string
) => `${sourceName || defaultCampaignName} - ${retargetLabel} - ${outcomeLabels.join(', ')}`;

export const createLocalTimeValue = (value?: string) => {
	const [hours = 0, minutes = 0, seconds = 0] = (value || '').split(':').map(Number);
	const time = new Date();
	time.setUTCHours(hours, minutes, seconds, 0);

	return time;
};

export default getVoiceCampaignChannelKey;
